import { createSelector, createSlice } from '@reduxjs/toolkit';

import {
  addLotAction,
  deleteLotAction,
  saveLotAction,
  RouletteState,
  setEditLotAttachmentsAction,
  setEditLotColorAction,
  setEditLotDescriptionAction,
  // setEditLotIdAction,
  setEditLotTitleAction,
  setGameProgressAction,
  setPriceAction,
  setResultsLotIndexAction,
  setResultsPostIdAction,
  setResultsPrizeAction,
  setRouletteAction,
  editLotAction,
} from './interface';
import { RootState } from '$store/rootReducer';
import { ColorsEnum } from '$shared/constants/colors';
import { Lot } from '$store/models';

const clearLot: Lot = {
  id: 0,
  title: '',
  color: ColorsEnum.BLUE,
  description: '',
  attachments: [],
};

const initialState: RouletteState = {
  editLot: null,
  price: '5',
  lots: [],
  lotIds: {},
  postIds: {},
  gameInProgress: null,
  prize: null,
  prizeIsShown: false,
  roulette: {} as any,
  lastRotationTime: 0,
};

export const { actions, reducer } = createSlice({
  name: 'roulette',
  initialState,
  reducers: {
    rotate(state) {
      state.lastRotationTime = Date.now();
    },
    createNewLot(state) {
      const colors = Object.values(ColorsEnum).reverse();

      const topColor = state.lots.reduce(
        (acc, lot) => {
          const color = lot.color;
          if (acc[color]) {
            acc[color]++;
          } else {
            acc[color] = 1;
          }
          return acc;
        },
        colors.reduce((acc, color) => ({ ...acc, [color]: 0 }), {}),
      );

      const color = (Object.keys(topColor) as ColorsEnum[]).reduce((a, b) =>
        topColor[a] < topColor[b] ? a : b,
      );

      state.editLot = {
        id: Date.now(),
        title: '',
        color,
        description: '',
        attachments: [],
      };
    },
    setPrice(state, action: setPriceAction) {
      state.price = action.payload;
    },
    addLot(state) {
      state.editLot && state.lots.push(state.editLot);
    },
    editLot(state, action: editLotAction) {
      state.editLot = state.lots.find((lot) => lot.id === action.payload) || clearLot;
    },
    stopEdit(state) {
      state.editLot = null;
    },
    saveLot(state, action: saveLotAction) {
      let edited = false;
      state.lots = state.lots.map((lot) => {
        if (lot.id === action.payload.id) {
          edited = true;
          return action.payload;
        } else {
          return lot;
        }
      });
      if (!edited) {
        state.lots.push(action.payload);
      }
      state.editLot = null;
    },
    setEditLotTitle(state, action: setEditLotTitleAction) {
      if (!state.editLot) state.editLot = clearLot;
      state.editLot.title = action.payload;
    },
    setEditLotDescription(state, action: setEditLotDescriptionAction) {
      if (!state.editLot) state.editLot = clearLot;
      state.editLot.description = action.payload;
    },
    setEditLotColor(state, action: setEditLotColorAction) {
      if (!state.editLot) state.editLot = clearLot;
      state.editLot.color = action.payload;
    },
    setEditAttachments(state, action: setEditLotAttachmentsAction) {
      if (!state.editLot) state.editLot = clearLot;
      state.editLot.attachments = action.payload;
    },
    deleteLot(state, action: deleteLotAction) {
      state.lots = state.lots.filter((l) => l.id !== action.payload);
    },
    clearLots(state) {
      state.lots = [];
    },
    clear() {
      return initialState;
    },
    // setEditLotId(state, action: setEditLotIdAction) {
    //   state.editLotId = action.payload;
    // },
    setGameProgress(state, action: setGameProgressAction) {
      state.gameInProgress = action.payload;
    },
    setPrize(state, action: setResultsPrizeAction) {
      state.prize = action.payload;
    },
    setPostId(state, action: setResultsPostIdAction) {
      const { postId, rouletteId } = action.payload;
      state.postIds[rouletteId] = postId;
    },
    showPrize(state) {
      state.prizeIsShown = true;
    },
    hidePrize(state) {
      state.prizeIsShown = false;
    },
    setResultsLotIndex(state, action: setResultsLotIndexAction) {
      state.lotIds[action.payload.rouletteId] = action.payload.lotIndex;
      state.lastRotationTime = Date.now();
    },
    setRoulette(state, action: setRouletteAction) {
      state.roulette = action.payload;
    },
  },
});

export { reducer as rouletteReducer, actions as rouletteActions };

export const rouletteSelector = (state: RootState): ReturnType<typeof reducer> =>
  state.roulette;

export const roulettePrizeSelector = (state: RootState) => state.roulette.prize;
export const rouletteGameInProgressSelector = (state: RootState) =>
  state.roulette.gameInProgress;

export const rouletteEditLotSelector = (state: RootState) => state.roulette.editLot;

export const rouletteLotsSelector = createSelector(
  rouletteSelector,
  (_, rouletteId: number) => rouletteId,
  (roulette, rouletteId) => {
    const id = roulette.lotIds[rouletteId];
    return id !== undefined ? id : null;
  },
);

export const roulettePostIdSelector = createSelector(
  rouletteSelector,
  (_, rouletteId: number) => rouletteId,
  (roulette, rouletteId) => {
    const id = roulette.postIds[rouletteId];
    return id !== undefined ? id : null;
  },
);
